import * as angular from 'angular';
import { StateParams, StateService } from '@uirouter/angularjs';

import * as _ from 'lodash';
import * as moment from 'moment';

import {
    AlarmList,
    ComponentDefinition,
    ConnectionPoint,
    Dashboard,
    Dictionary,
    DigitalSetSimpleDictionary,
    Group,
    HeaderStyle,
    IAction,
    IGroupTreeNode,
    IValueViewModel,
    Identifier,
    ObjectAccessRights,
    Page,
    PdfExportConfiguration,
    PrintWidget,
    RefreshClickData,
    TagDateValue,
    TagDefinition,
    TagStringValueDto
} from '../../../models';
import { IRootScope } from 'IRootScope';
import {
    ExportToPdfService,
    IConfigurationService,
    IDashboardLoadService,
    IDashboardSeriesService,
    IDateRangeService,
    IGroupSelectionService,
    IGroupService,
    ILoggerService,
    IPrintConfiguration,
    IPrintOptionsService,
    ISessionStorageService,
    ISignalrService,
    IStoreService,
    IStringValuesService,
    PrintOptions
} from '../../../services';
import { orientation, PageOption } from '../../../services/helpers/print';
import { IPrintHelper, toggleEditMode, workspaceKey, refreshClick, groupSelected, whiteBackgroundColor, backButtonClick } from '../../../common';
import { IDashboardStateService } from '../../dashboard/dashboardState.service';
import { DashboardStateMapper } from '../../dashboard/DashboardStateMapper';
import { getTimeZone } from '../../../common/getTimezone';
import { pixToMm } from '../../../services/helpers/print/constants';
import { getWidgetClass } from '../../../common/helpers/getWidgetClass';
import { Store, StoreElement } from '../../../stateManagement';

class PrintDashboardStateController implements angular.IComponentController, DashboardStateMapper {
    accessRights: ObjectAccessRights[];
    actions: Dictionary<IAction>;
    alarmList: AlarmList;
    applicationName: string;
    beginDate: Date;
    connectionPointsDictionary: { [guid: string]: ConnectionPoint };
    dashboardGuid: string;
    dashboardName: string;
    dashboards: Dashboard[];
    data: { guid: string; values: TagDateValue[] }[];
    digitalSets: DigitalSetSimpleDictionary;
    endDate: Date;
    exportPdfConfig: PdfExportConfiguration = {} as any;
    formController: angular.IFormController;
    groupDictionary: _.Dictionary<Group>;
    groupGuid: string;
    groups: Group[];
    identifiers: Identifier[];
    isLoading: boolean;
    isRequestFromAPI: boolean = false;
    loadingMessage: string;
    logoFileName: string;
    options: PageOption[] = [];
    pages: Page[];
    printConfig: IPrintConfiguration;
    selectedGroup: Group;
    store: Store;
    tagsDictionary: { [guid: string]: TagDefinition };
    timeZone: string;
    userName: string;
    stringValues: TagStringValueDto[];
    viewModels: Dictionary<IValueViewModel>;
    workspace: any;

    readonly printing: boolean;

    private _pageHeight = 210; // mm
    private _pageWidth = 297; // mm
    private _pixToMm = pixToMm; // px/mm
    private _widgetMargin = 10;
    private _adapter: number = 10;
    private _widgetList: PrintWidget[];
    private _formats = [];
    private _garbages: (() => void)[] = [];

    static $inject = [
        '$scope',
        '$rootScope',
        '$translate',
        '$state',
        '$stateParams',
        '$q',
        '$window',
        'dateRangeService',
        'sessionStorageService',
        'printHelper',
        'configurationService',
        'printOptionsService',
        'groupSelectionService',
        'dashboardLoadService',
        'loggerService',
        'dashboardSeriesService',
        'groupService',
        'dashboardStateService',
        'signalrService',
        'exportToPdfService',
        'stringValuesService',
        'storeService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$translate: angular.translate.ITranslateService,
        private _$state: StateService,
        private _$stateParams: StateParams,
        private _$q: angular.IQService,
        private _$window: angular.IWindowService,
        private _dateRangeService: IDateRangeService,
        private _sessionStorageService: ISessionStorageService,
        private _printHelper: IPrintHelper,
        private _configurationService: IConfigurationService,
        private _printOptionsService: IPrintOptionsService,
        private _groupSelectionService: IGroupSelectionService,
        private _dashboardLoadService: IDashboardLoadService,
        private _loggerService: ILoggerService,
        private _dss: IDashboardSeriesService,
        private _groupService: IGroupService,
        private _dashboardStateService: IDashboardStateService,
        private _signalrService: ISignalrService,
        private _exportToPdfService: ExportToPdfService,
        private _stringValuesService: IStringValuesService,
        private _storeService: IStoreService
    ) {
    }

    $onInit() {
        // set in edit Mode
        if (!this._$rootScope.isEditMode) {
            this._$rootScope.$broadcast(toggleEditMode, true);
        }

        this._storeService.register(
            store => this.userName = store.authentication.value.userName,
            [StoreElement.Authentication]
        );

        const beginDate = this._$state.params.beginDate;
        this.beginDate = new Date(beginDate);
        const endDate = this._$state.params.endDate;
        this.endDate = new Date(endDate);
        this.timeZone = getTimeZone();

        const currentLanguage = this._$translate.use();
        let lang = this._$stateParams.lang;
        if (!lang && !currentLanguage) {
            lang = 'en';
        }
        if (lang !== currentLanguage) {
            this._$translate.use(lang);
            moment.locale(lang);
        }


        this.applicationName = 'WiDE';
        this._widgetList = [];
        this.pages = [];
        this.options = this._printOptionsService.getOptions();
        this._formats = this._printOptionsService.getFormats();
        this.dashboardGuid = this._$state.params.guid;
        this.groupGuid = this._$state.params['groupGuid'];

        this.workspace = JSON.parse(this._sessionStorageService.getFrom(workspaceKey));
        this.isRequestFromAPI = (this._$state.current.name === 'standAlonePrintDashboard');

        // default print options
        this.printConfig = {
            headerStyle: HeaderStyle.Default,
            isHidingBorder: false,
            backgroundColor: whiteBackgroundColor,
            printOptions: this.getDefaultPrintOptions(),
            isUseNewPrintSystem: false
        };

        if (this.isRequestFromAPI) {
            this._adapter = 5;
            this._signalrService.disconnect();
        } else {
            this._adapter = 10;
        }

        this._pageWidth -= this._adapter * this._pixToMm; //horizontal margins
        this._pageHeight -= this._adapter * this._pixToMm; //vertical margins

        if (this._groupSelectionService.selectedGroup()) {
            this.selectedGroup = this._groupSelectionService.selectedGroup();
        }

        this._configurationService.getLogoFileName().then(data => {
            if (data) {
                this.logoFileName = data;
            }
        });

        const connect = this._dashboardStateService.connect(this);
        this._garbages.push(connect.unsubscribe);
        this.isLoading = true;
        connect.onLoad
            .then(() => this.loadDashboard())
            .then(() => this.initWatchers())
            .then(() => this.loadData(beginDate, endDate))
            .finally(() => this.isLoading = false);

        this._garbages.push(this._$rootScope.$on(backButtonClick, () => this.dashboard()));
    }

    $onDestroy() {
        this._garbages.forEach(g => g());
    }

    private updatePrintOptions(newValue: PrintOptions) {
        if (newValue) {
            const size = this._formats[newValue.format];
            this._pageWidth = size.width - this._adapter * this._pixToMm;
            this._pageHeight = size.height - this._adapter * this._pixToMm;
            //portrait sizes
            if (newValue.orientation === orientation.Landscape) {
                this.switchOrientation();
            }
            this.updateWidgetsSize();
            this.exportPdfConfig.printOptions = newValue;
        }
    }

    // watcher should only be init once AFTER first loading
    // as all configuration is setted in situ multiple times
    // and provokes multiple refresh
    private initWatchers(): angular.IPromise<void> {
        this._$scope.$watch(
            () => this.printConfig.printOptions,
            newValue => this.updatePrintOptions(newValue)
        );

        this._$scope.$watchGroup(
            [
                () => this.printConfig.headerStyle
            ],
            (newValue, oldValue) => {
                if (newValue !== oldValue) {
                    this.updateWidgetsSize();
                }
            }
        );

        return this._$q.when();
    }

    private loadDashboard(): angular.IPromise<any> {
        return this.getGroup(this.groupGuid)
            .then(() => this._dashboardLoadService.loadInfo(this._$stateParams.guid))
            .then(data => {
                this.setDashboardName(data.baseName);
                this.exportPdfConfig.isUseNewPrintSystem = false;
                this.exportPdfConfig.dashboard = {
                    guid: this._$stateParams.guid,
                    name: data.baseName
                };

                if (data.configuration && data.configuration.printConfiguration) {
                    data.configuration.printConfiguration.isUseNewPrintSystem = false;
                    // work on a new object to trigger onChange
                    const printConfig = angular.copy(data.configuration.printConfiguration);
                    const urlConfig = this.getConfigFromUrl();
                    if (urlConfig) {
                        printConfig.printOptions = urlConfig.configuration;
                    }
                    try {
                        const opt = printConfig.printOptions;
                        printConfig.printOptions = this.options
                            .map(o => o.configuration)
                            .filter(o => o.orientation === opt.orientation && o.format === opt.format)[0];
                    } catch (exception) {
                        this._loggerService.logWarning('Cannot reload print orientation');
                        printConfig.printOptions = (this.options[0] as any).configuration;
                    }
                    this.printConfig = printConfig;
                }
                return this._$q.when(this.selectedGroup
                    ? this._dss
                        .getSeries(data.widgetList, data.referenceGroup, this.selectedGroup)
                        .then(series => this._dashboardLoadService.mapWidgetList(data.widgetList, series) as PrintWidget[])
                    : data.widgetList as PrintWidget[]);
            })
            .then(widgetList => {
                widgetList.forEach(w => {
                    if (w.series) {
                        w.series.forEach(s => delete (s.attributes));
                    }
                });
                this._widgetList = widgetList;
                this._widgetList = _.sortBy(this._widgetList, w => w.row);
            });
    }

    private loadData(beginDate: string, endDate: string): angular.IPromise<void> {
        return this._dashboardLoadService
            .refresh(
                this._widgetList,
                beginDate,
                endDate,
                this.selectedGroup ? this.selectedGroup.guid : undefined,
                this.printing,
                this.dashboardGuid
            )
            .then(data => {
                // Could be improved by recovering only String Tags, then passing a distinct array of all value to getByIds method
                // No effort invested in old print method
                this._stringValuesService
                    .getAll()
                    .then(response => {
                        this.stringValues = response;
                        return data;
                    })
                    .then(data => {
                        data = this._dashboardLoadService.setIdentifiers(data, this.viewModels);
                        this._dashboardLoadService.assignValues(data, this._widgetList);
                        this._dateRangeService.beginDate(beginDate);
                        this._dateRangeService.endDate(endDate);
                        this._dateRangeService.initialized(true);
                        this.exportPdfConfig.beginDate = beginDate;
                        this.exportPdfConfig.endDate = endDate;
                        const d: RefreshClickData = { beginDate: beginDate, endDate: endDate };
                        this._$rootScope.$broadcast(refreshClick, d);
                    });
            });
    }

    private getGroup(groupGuid): angular.IPromise<any> {
        if (groupGuid) {
            return this._groupService
                .getByGuid(groupGuid)
                .then(data => {
                    if (data.group) {
                        this.selectedGroup = data.group;
                        this.dashboardName += ` - ${this.selectedGroup.uniqueName}`;
                        this._groupSelectionService.selectedGroup(this.selectedGroup);
                        this._$rootScope.$broadcast(groupSelected, { guid: this.selectedGroup.guid });
                        this.exportPdfConfig.group = this.selectedGroup as IGroupTreeNode;
                    }
                });
        } else {
            return this._$q.when();
        }
    }

    private setDashboardName(baseName: string) {
        let name = baseName;
        if (this.selectedGroup) {
            name += ` - ${this.selectedGroup.uniqueName}`;
        }
        this.dashboardName = name;
    }

    private switchOrientation() {
        const tmp = this._pageWidth;
        this._pageWidth = this._pageHeight;
        this._pageHeight = tmp;
    }

    private updateWidgetsSize() {
        const colCount = this.printConfig.printOptions.columns;
        const sizeInPixels = this._printHelper.computeSizeInPixels(this._widgetMargin, this.printConfig, this._adapter);
        const printableWidgets = this._printHelper.changeColumnNumber(this._widgetList, colCount);

        const rowsCount = this._printHelper.rowsCount(this.printConfig, this._widgetMargin, 20 * 0.7, this._adapter);
        const printable = this._printHelper.createPrintableDashboard(
            printableWidgets,
            rowsCount,
            colCount,
            this._widgetMargin,
            sizeInPixels.width,
            sizeInPixels.height
        );
        printableWidgets.forEach(w => this.setWidgetStyle(w));
        this.pages = printable.pages;
    }

    private setWidgetStyle(w: PrintWidget) {
        const style = w.style || {};
        style['background-color'] = w.backgroundColor;
        w.style = style;
    }

    save(): angular.IPromise<void> {
        return this._dashboardStateService
            .loadDashboard(this.dashboardGuid)
            .then(dashboard => {
                dashboard.configuration.printConfiguration = this.printConfig;
                return this._dashboardStateService.updateDashboard(dashboard, true);
            })
            .then(() => {
                this.exportPdfConfig.isUseNewPrintSystem = this.printConfig.isUseNewPrintSystem;
                this._loggerService.logSuccess('Configuration saved');
            })
            .catch(() => {
                this._loggerService.logError('Error saving print configuration');
            });
    }

    exportToPdf(): void {
        this._exportToPdfService
            .print(this.exportPdfConfig, this._$translate.use());
    }

    //TODO copy/paste from wtWidgetList
    getClass(item: any): any {
        return getWidgetClass(item);
    }

    print() {
        this._$window.print();
        return false;
    }

    private dashboard() {
        this._$state.go('dashboard', {
            dashboardName: this._$stateParams.guid,
            beginDate: this._$stateParams.beginDate,
            endDate: this._$stateParams.endDate
        });
        this._$rootScope.$broadcast(toggleEditMode, false);
    }

    getPrintStyle() {
        const widthInPx = this._pageWidth * this._pixToMm;
        const heightInPx = this._pageHeight * this._pixToMm;
        return {
            'width': widthInPx + 'px',
            'height': heightInPx + 'px',
            'background-color': this.printConfig ? this.printConfig.backgroundColor : 'none'
        };
    }

    getPageClass() {
        return {
            'portrait': this.printConfig.printOptions.orientation === 0,
            'landscape': this.printConfig.printOptions.orientation === 1,
            'A4': this.printConfig.printOptions.format === 0,
            'A3': this.printConfig.printOptions.format === 1,
            'letter': this.printConfig.printOptions.format === 2,
            'compact-header': this.printConfig && this.printConfig.headerStyle === HeaderStyle.Compact
        };
    }

    getDefaultPrintOptions(): PrintOptions {
        const config = this.getConfigFromUrl();
        return (config) ? config.configuration : (this.options[0] as any).configuration;
    }

    onPrintSystemChange() {
        const url = 'scaledPrintPreview';
        this._$state.go(url, {
            guid: this.dashboardGuid,
            groupGuid: this.selectedGroup?.guid,
            beginDate: this._$stateParams.beginDate,
            endDate: this._$stateParams.endDate
        });
    }

    private getConfigFromUrl() {
        const config = _.find(this.options, o =>
            o.configuration.orientation === parseInt(this._$state.params['orientation'])
            && (o.configuration as any).format === parseInt(this._$state.params['format'])
        );
        return config;
    }
}

export const wtPrintDashboardState: ComponentDefinition = {
    componentId: 'wtPrintDashboardState',
    controller: PrintDashboardStateController,
    template: require('./printDashboardState.component.html'),
    controllerAs: 'vm',
    bindings: {
        // from route
        printing: '<'
    }
};